import { css, styled } from "styled-components";
import type {
  TabsPayload,
  TitleStyledPayload,
  TitlesStyledPayload,
} from "../types";

const backgroundLine = (edge: "right" | "left" | "bottom", value: string) => {
  const size =
    edge === "bottom"
      ? "width: 100%; height: 1px;"
      : "width: 1px; height: 100%;";
  const pos =
    edge === "right"
      ? "right: 0; top: 0;"
      : edge === "left"
        ? "left: 0; top: 0;"
        : "bottom: 0; left: 0;";
  return `
    position: relative;
    &::before {
      content: "";
      position: absolute;
      ${pos}
      ${size}
      background: ${value};
      z-index: 0;
    }
  `;
};

export const STYLED_TITLE_ATTRIBUTES = ({
  settings,
}: {
  settings: TabsPayload;
}) => ({
  $color: settings.tabsColor,
  $hoverColor: settings.tabsHoverColor,
  $hoverBackground: settings.tabsBgHoverColor,
  $font: settings.tabsFontSize,
  $borderWidth: settings.tabsBorderWidth,
  $borderColor: settings.tabsBorderColor,
  $borderColorHover: settings.tabsBorderHoverColor,
  $rounded: settings.tabsBorderRadius,
  $design: settings.tabsDesign,
  $background: settings.tabsBgColor,
  $layout: settings.tabsLayout,
});

const firstDesignRounded = ({
  $background,
  $rounded,
  $borderWidth,
  $borderColor,
}: {
  $background: string;
  $rounded: string;
  $borderWidth: number;
  $borderColor: string;
}) => css`
  background: ${$background};
  border-color: ${$borderColor};
  border-style: solid;

  border-width: ${`${$borderWidth / 2}px`};
  &[data-first-in-row="true"] {
    border-left-width: ${`${$borderWidth}px`};
  }
  &[data-last-in-row="true"] {
    border-right-width: ${`${$borderWidth}px`};
  }
  &[data-in-first-row="true"] {
    border-top-width: ${`${$borderWidth}px`};
  }
  &[data-in-last-row="true"] {
    border-bottom-width: ${`${$borderWidth}px`};
  }
  &[data-first-in-row="true"][data-in-first-row="true"] {
    border-top-left-radius: ${$rounded};
  }
  &[data-first-in-row="true"][data-in-last-row="true"] {
    border-bottom-left-radius: ${$rounded};
  }
  &[data-last-in-row="true"][data-in-first-row="true"] {
    border-top-right-radius: ${$rounded};
  }
  &[data-last-in-row="true"][data-in-last-row="true"] {
    border-bottom-right-radius: ${$rounded};
  }
`;

const secondDesignLayout = ({
  $background,
  $layout,
}: {
  $background: string;
  $layout: string;
}) => {
  let borderStyle = "";
  let pseudoElementPosition = "";
  const topStyleBorder = `border-bottom: 1px solid ${$background} !important;`;
  const topStylePseudo = "bottom: -2px; left: 0; width: 100%; height: 3px;";

  switch ($layout) {
    case "left":
      borderStyle = backgroundLine("right", $background);
      pseudoElementPosition = "right: -2px; top: 0; height: 100%; width: 3px;";
      break;
    case "right":
      borderStyle = backgroundLine("left", $background);
      pseudoElementPosition = "left: -2px; top: 0; height: 100%; width: 3px;";
      break;
    case "top":
    case "bottom":
      borderStyle = topStyleBorder;
      pseudoElementPosition = topStylePseudo;
      break;
    default:
      return;
  }

  return css`
    border-color: unset;
    border-width: 0;
    ${borderStyle}
    &::after {
      ${pseudoElementPosition}
    }
    /* Mobile: side layouts (left/right) behave as top — indicator only at bottom, same color as desktop */
    @media screen and (max-width: 768px) {
      ${["left", "right"].includes($layout)
        ? css`
            border-right: none !important;
            border-left: none !important;
            border-bottom: 1px solid ${$background} !important;
            &::after {
              top: auto;
              right: auto;
              bottom: -2px;
              left: 0;
              width: 100%;
              height: 3px;
            }
          `
        : ""}
    }
  `;
};

export const TitleStyled = styled.div<TitleStyledPayload>`
  .material-icons {
    font-family: "Material Icons";
  }

  color: ${({ $color }) => $color};
  font-size: ${({ $font }) => `${$font}px`};
  border-width: ${({ $borderWidth = 0 }) => `${$borderWidth}px`};
  border-color: ${({ $borderColor }) => $borderColor || "transparent"};
  border-radius: ${({ $rounded }) =>
    $rounded === "50px" ? `calc(${$rounded || "0px"} / 2)` : $rounded};
  &:hover,
  &.--active {
    color: ${({ $hoverColor }) => $hoverColor};
    background: ${({ $hoverBackground }) => $hoverBackground};
    border-color: ${({ $borderColorHover }) =>
      $borderColorHover || "transparent"};
  }
  @media screen and (max-width: 768px) {
    font-size: var(--paragraph-mobile-size) !important;
  }

  ${({ $design, $background }) => {
    if ($design !== "design-4") return;
    return css`
      background: ${$background};
    `;
  }}

  ${({ $design, $hoverBackground, $background, $layout }) =>
    $design === "design-2" &&
    css`
      border-radius: 0;
      ${secondDesignLayout({ $background, $layout })}
      &:hover,
      &.--active {
        background: transparent;
        &::after {
          background: ${$hoverBackground};
        }
      }
    `}

  ${({ $design, $rounded, $background, $borderWidth, $borderColor }) => {
    if ($design !== "design-1") return;

    return firstDesignRounded({
      $background,
      $rounded,
      $borderWidth,
      $borderColor,
    });
  }}
`;

const layoutToMargin = {
  top: "margin-bottom",
  bottom: "margin-top",
  left: "margin-right",
  right: "margin-left",
};

export const TitlesStyled = styled.div<TitlesStyledPayload>`
  background-color: ${({ $background }) => $background};
  border-radius: ${({ $rounded }) => $rounded};

  grid-template-columns: ${({ $row }) =>
    $row === "auto"
      ? css`repeat(auto-fill, minmax(20%, 1fr))`
      : css`repeat(${$row || 1}, 1fr)`};

  ${({ $layout }) => {
    if (!layoutToMargin[$layout]) return;
    return css`
      ${layoutToMargin[$layout]}: 32px;
      /* Mobile: side layouts behave as top — use margin-bottom instead of margin-right/left */
      @media screen and (max-width: 768px) {
        ${["left", "right"].includes($layout)
          ? css`
              margin-right: 0;
              margin-left: 0;
              margin-bottom: 32px;
            `
          : ""}
      }
    `;
  }}

  ${({ $design, $background }) => {
    if ($design !== "design-2") return;
    return css`
      .tab-placeholder-gh08QFXRG3 {
        ${backgroundLine("bottom", $background)}
      }
    `;
  }}
  
  ${({ $design, $background }) => {
    if ($design !== "design-1") return;
    return css`
      .tab-placeholder-gh08QFXRG3 {
        background: ${$background};
      }
    `;
  }}

  ${({ $design, $rounded, $background, $borderWidth, $borderColor }) => {
    if ($design !== "design-1") return;
    return css`
      .tab-placeholder-gh08QFXRG3 {
        ${firstDesignRounded({
          $background,
          $rounded,
          $borderWidth,
          $borderColor,
        })}
      }
    `;
  }}
`;
